<template>
    <div>
        <section class="tabs-section">
            <NavBtns @navigatetotab="navigateToTab" :combo-ids="comboIds" :tabs="tabs" />
            <FlowNavigation currentStep="AddonsNew" formId="addons-form" @submit="submit" />
        </section>

        <section class="addons-section py-7">
            <div class="addons-container container">
                <div class="addons-content flex flex-wrap -mx-3">
                    <div class="addons-left w-full lg:w-2/3 max-w-full px-3">
                        <h2 class="section-title text-lg lg:text-[22px] text-[var(--charcoal)] font-bold mb-3">
                            Additional Options</h2>

                        <!-- Loading State -->
                        <div v-if="loading" class="loading-state">
                            <div class="spinner"></div>
                            <p>Loading add-ons...</p>
                        </div>

                        <!-- Addons Fields -->
                        <div v-else-if="hasAddons" class="addons-form">
                            <!-- Total Cost Summary (mobile only — desktop shows the live total in the sticky Booking Summary sidebar) -->
                            <div v-if="calculateAddonsSubtotal > 0" class="total-cost-summary lg:hidden bg-[linear-gradient(111.97deg,_#F4ECE1_0%,_#F5F3F0_100%)] rounded-2xl border border-[var(--bone)] p-4 mb-6 shadow-[0px_1.4px_2.8px_0px_#0000000D]">
                                <div class="flex items-center justify-between">
                                    <span class="total-cost-label text-lg text-[var(--charcoal-dark)] font-semibold">Total Add-on Cost:</span>
                                    <span class="total-cost-amount bg-[var(--light-beige)] text-[var(--dark-brownish-gray)] font-bold px-4 py-2 rounded-full text-lg">{{ currencyFormat(calculateAddonsSubtotal) }}</span>
                                </div>
                            </div>

                            <!-- Price per pax fields grouped by person -->
                            <template v-if="pricePerPaxMatrix.length > 0">
                                <h3 class="addons-group-title text-sm uppercase tracking-wide text-[var(--tone-gray)] font-semibold mb-2">
                                    Per-Passenger Options</h3>
                                <div v-for="(personRow, rowIndex) in pricePerPaxMatrix" :key="personRow.person.paxIndex"
                                    class="person-group border border-[var(--bone)] bg-white shadow-[0px_1.4px_2.8px_0px_#0000000D] rounded-2xl mb-4 overflow-hidden pb-2">
                                    <!-- Person Header (shown once for all their fields) -->
                                    <div
                                        class="person-group-header bg-[linear-gradient(111.97deg,_#F4ECE1_0%,_#F5F3F0_100%)] py-2 px-3 border-b border-[var(--bone)] flex items-center justify-between">
                                        <span
                                            class="person-badge text-lg text-[var(--charcoal-dark)] font-medium">{{
                                            personRow.person.displayName }}</span>
                                        <span v-if="personRow.person.total > 0"
                                            class="price-badge bg-[var(--light-beige)] text-[var(--dark-brownish-gray)] font-semibold px-3 py-1 rounded-full text-sm">
                                            {{ currencyFormat(personRow.person.total) }}
                                        </span>
                                        <span v-else
                                            class="price-badge bg-[var(--light-beige)] text-[var(--dark-brownish-gray)] font-semibold px-3 py-1 rounded-full text-sm">
                                            No Charge
                                        </span>
                                    </div>

                                    <!-- All price_per_pax fields for this person -->
                                    <div v-for="field in personRow.fields"
                                        :key="`${field.id}-${personRow.person.paxIndex}`"
                                        class="field-card parent-per-pax py-2 px-3">
                                        <div class="field-header flex items-center justify-between">
                                            <div class="flex items-center">
                                                <label :for="`field-${field.id}-${rowIndex}`"
                                                    class="field-label inline-block text-lg text-[var(--charcoal-dark)] font-medium mb-1">
                                                    {{ field.label }}
                                                    <span v-if="field.required" class="required-indicator">*</span>
                                                </label>
                                                <button 
                                                    v-if="field.description" 
                                                    @click="toggleDescription(`${field.id}-${rowIndex}`)"
                                                    class="info-icon-btn ml-2 p-1 rounded-full hover:bg-gray-100 transition-colors"
                                                    type="button"
                                                    :aria-label="`Show description for ${field.label}`">
                                                    <svg class="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                                                    </svg>
                                                </button>
                                            </div>
                                            <span
                                                v-if="field.additional_fee && field.price && !['radio', 'dropdown'].includes(field.type)"
                                                class="price-badge bg-[var(--light-beige)] text-[var(--dark-brownish-gray)] font-semibold px-3 py-1 rounded-full text-sm ml-2">
                                                {{ currencyFormat(field.price) }}
                                            </span>
                                        </div>

                                        <!-- Description toggle -->
                                        <div v-if="field.description && showDescriptions[`${field.id}-${rowIndex}`]" 
                                             class="field-description text-sm text-gray-600 mb-2 p-2 bg-gray-50 rounded-lg border-l-4 border-blue-200">
                                            {{ field.description }}
                                        </div>

                                        <!-- Parent Input -->
                                        <component :is="getInputComponent(field.type)" :field="field"
                                            :value="getParentPerPaxValue(field, rowIndex)" :enabled="true"
                                            v-bind:display_errors="display_errors"
                                            v-bind:error="errors[`${field.id}-${rowIndex + 1}`]" :id-suffix="`-${rowIndex}`"
                                            @update:value="updateParentPerPaxValue(field, rowIndex, $event)"
                                            @validate="validateField(field, `${rowIndex}`)" />

                                        <!-- Nested/Conditional Children for this parent instance -->
                                        <div v-if="shouldShowChildrenForPerPaxParent(field, rowIndex)"
                                            class="nested-fields">
                                            <div v-for="child in sortedChildren(field.children)"
                                                :key="`${child.id}-p${rowIndex}`" class="child-field-card pt-3 pl-6">
                                                <div class="field-header flex items-center justify-between">
                                                    <div class="flex items-center">
                                                        <label :for="`field-${child.id}-p${rowIndex}`"
                                                            class="field-label inline-block text-lg text-[var(--charcoal-dark)] font-medium mb-1">
                                                            {{ child.label }}
                                                            <span v-if="child.required" class="required-indicator">*</span>
                                                        </label>
                                                        <button 
                                                            v-if="child.description" 
                                                            @click="toggleDescription(`${child.id}-p${rowIndex}`)"
                                                            class="info-icon-btn ml-2 p-1 rounded-full hover:bg-gray-100 transition-colors"
                                                            type="button"
                                                            :aria-label="`Show description for ${child.label}`">
                                                            <svg class="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                    <span
                                                        v-if="child.additional_fee && child.price && !['radio', 'dropdown'].includes(child.type)"
                                                        class="price-badge bg-[var(--light-beige)] text-[var(--dark-brownish-gray)] font-semibold px-3 py-1 rounded-full text-sm">
                                                        {{ currencyFormat(child.price) }}
                                                    </span>
                                                </div>

                                                <!-- Description toggle -->
                                                <div v-if="child.description && showDescriptions[`${child.id}-p${rowIndex}`]" 
                                                     class="field-description text-sm text-gray-600 mb-2 p-2 bg-gray-50 rounded-lg border-l-4 border-blue-200">
                                                    {{ child.description }}
                                                </div>

                                                <!-- Child input for this parent instance -->
                                                <component :is="getInputComponent(child.type)" :field="child"
                                                    :value="getChildValueForPerPaxParent(child, rowIndex)"
                                                    :enabled="true" v-bind:display_errors="display_errors"
                                                    v-bind:error="errors[`${child.id}-p${rowIndex + 1}`]"
                                                    :id-suffix="`-p${rowIndex}`"
                                                    @update:value="updateChildValueForPerPaxParent(child, rowIndex, $event)"
                                                    @validate="validateField(child, `p${rowIndex}`)" />
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Person Subtotal (all fields for this person) -->
                                    <div v-if="getPersonSubtotal(rowIndex) > 0"
                                        class="person-subtotal-tag text-sm text-[var(--charcoal-dark)] font-bold px-3 pt-2 mt-1 border-t border-[var(--bone)]">
                                        Person Total: {{ currencyFormat(getPersonSubtotal(rowIndex)) }}
                                    </div>
                                </div>
                            </template>

                            <!-- Regular fields (non price_per_pax, plus display_once price_per_pax fields
                                 which are answered once here even though they're still priced/stored
                                 per traveler) - Grouped in container -->
                            <template v-if="sortedFields.some(f => shouldShowField(f) && (f.unit_type !== 'price_per_pax' || f.display_once))">
                                <h3 v-if="pricePerPaxMatrix.length > 0" class="addons-group-title text-sm uppercase tracking-wide text-[var(--tone-gray)] font-semibold mb-2">
                                    Trip Options</h3>
                                <div
                                    class="regular-addons-section border border-[var(--bone)] bg-white shadow-[0px_1.4px_2.8px_0px_#0000000D] rounded-2xl mb-4 overflow-hidden pb-2">
                                <div v-for="field in sortedFields" :key="field.id">
                                    <!-- Regular parent field (not Price per pax, or a display_once price_per_pax field) -->
                                    <div v-if="shouldShowField(field) && (field.unit_type !== 'price_per_pax' || field.display_once)"
                                        class="field-card py-2 px-3 border-b border-[var(--bone)] last:border-b-0">
                                        <div class="field-header flex items-center justify-between mb-2">
                                            <div class="flex items-center flex-1">
                                                <label :for="`field-${field.id}`"
                                                    class="field-label inline-block text-lg text-[var(--charcoal-dark)] font-medium mb-1">
                                                    {{ field.label }}
                                                    <span v-if="field.required" class="required-indicator">*</span>
                                                </label>
                                                <button 
                                                    v-if="field.description" 
                                                    @click="toggleDescription(field.id)"
                                                    class="info-icon-btn ml-2 p-1 rounded-full hover:bg-gray-100 transition-colors"
                                                    type="button"
                                                    :aria-label="`Show description for ${field.label}`">
                                                    <svg class="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                                                    </svg>
                                                </button>
                                            </div>
                                            <span
                                                v-if="field.additional_fee && field.price && !['radio', 'dropdown'].includes(field.type)"
                                                class="price-badge bg-[var(--light-beige)] text-[var(--dark-brownish-gray)] font-semibold px-3 py-1 rounded-full text-sm ml-2 flex-shrink-0">
                                                {{ currencyFormat(field.price) }}{{ perPersonSuffix(field) }}
                                            </span>
                                        </div>

                                        <!-- Description toggle -->
                                        <div v-if="field.description && showDescriptions[field.id]"
                                             class="field-description text-sm text-gray-600 mb-2 p-2 bg-gray-50 rounded-lg border-l-4 border-blue-200">
                                            {{ field.description }}
                                        </div>

                                        <!-- Dynamic Input Component -->
                                        <component :is="getInputComponent(field.type)" :field="field"
                                            :value="getFieldValue(field)" :enabled="true"
                                            v-bind:display_errors="display_errors" v-bind:error="errors[field.id]"
                                            @update:value="updateValue(field, $event)" @validate="validateField(field)" />

                                        <!-- Nested/Conditional Children -->
                                        <div v-if="shouldShowChildren(field)" class="nested-fields mt-3">
                                            <div v-for="child in sortedChildren(field.children)" :key="child.id"
                                                class="child-field-card pl-3 py-2 border-l-2 border-[var(--bone)]">
                                                <div class="field-header flex items-center justify-between mb-2">
                                                    <div class="flex items-center">
                                                        <label :for="`field-${child.id}`"
                                                            class="field-label inline-block text-lg text-[var(--charcoal-dark)] font-medium mb-1">
                                                            {{ child.label }}
                                                            <span v-if="child.required" class="required-indicator">*</span>
                                                        </label>
                                                        <button 
                                                            v-if="child.description" 
                                                            @click="toggleDescription(child.id)"
                                                            class="info-icon-btn ml-2 p-1 rounded-full hover:bg-gray-100 transition-colors"
                                                            type="button"
                                                            :aria-label="`Show description for ${child.label}`">
                                                            <svg class="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                    <span
                                                        v-if="child.additional_fee && child.price && !['radio', 'dropdown'].includes(child.type)"
                                                        class="price-badge bg-[var(--light-beige)] text-[var(--dark-brownish-gray)] font-semibold px-3 py-1 rounded-full text-sm ml-2 flex-shrink-0">
                                                        {{ currencyFormat(child.price) }}
                                                    </span>
                                                </div>

                                                <!-- Description toggle -->
                                                <div v-if="child.description && showDescriptions[child.id]" 
                                                     class="field-description text-sm text-gray-600 mb-2 p-2 bg-gray-50 rounded-lg border-l-4 border-blue-200">
                                                    {{ child.description }}
                                                </div>

                                                <!-- Repeated child fields for Price per pax (not for display_once
                                                     parents — their children are answered once, same as any
                                                     regular field's children) -->
                                                <template
                                                    v-if="field.unit_type === 'price_per_pax' && !field.display_once && personsList.length > 0">
                                                    <div v-for="(person, personIndex) in personsList" :key="person.paxIndex"
                                                        class="person-input">
                                                        <div class="person-header">{{ person.displayName }}</div>
                                                        <component :is="getInputComponent(child.type)" :field="child"
                                                            :value="getChildValue(child, personIndex)" :enabled="true"
                                                            v-bind:display_errors="display_errors"
                                                            v-bind:error="errors[`${child.id}-${personIndex}`]"
                                                            :id-suffix="`-${personIndex}`"
                                                            @update:value="updateChildValue(child, personIndex, $event)"
                                                            @validate="validateField(child, `${personIndex}`)" />
                                                    </div>
                                                </template>

                                                <!-- Repeated child fields for Price per unit -->
                                                <template v-else-if="field.unit_type === 'price_per_unit'">
                                                    <div v-for="unitIndex in getRepeatCount(field)" :key="unitIndex"
                                                        class="unit-input">
                                                        <div class="unit-header">Unit {{ unitIndex }}</div>
                                                        <component :is="getInputComponent(child.type)" :field="child"
                                                            :value="getChildValue(child, unitIndex - 1)" :enabled="true"
                                                            v-bind:display_errors="display_errors"
                                                            v-bind:error="errors[`${child.id}-${unitIndex}`]"
                                                            :id-suffix="`-${unitIndex}`"
                                                            @update:value="updateChildValue(child, unitIndex - 1, $event)"
                                                            @validate="validateField(child, `${unitIndex}`)" />
                                                    </div>
                                                </template>

                                                <!-- Regular child (not repeated) -->
                                                <template v-else>
                                                    <component :is="getInputComponent(child.type)" :field="child"
                                                        :value="getFieldValue(child)" :enabled="true"
                                                        v-bind:display_errors="display_errors"
                                                        v-bind:error="errors[child.id]"
                                                        @update:value="updateValue(child, $event)"
                                                        @validate="validateField(child)" />
                                                </template>
                                            </div>
                                        </div>

                                        <!-- Field Subtotal Display -->
                                        <div v-if="!field.parent_field_id && safeValues[field.id]?.subtotal > 0"
                                            class="subtotal-tag text-sm text-[var(--dark-brownish-gray)] font-semibold mt-2 pt-2 border-t border-[var(--bone)]">
                                            Subtotal: {{ currencyFormat(safeValues[field.id].subtotal) }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </template>
                        </div>

                        <!-- No Addons Message -->
                        <div v-else class="no-addons-message">
                            <p>No additional options available for this tour package.</p>
                        </div>
                    </div>

                    <div class="addons-right w-full lg:w-1/3 max-w-full px-3 lg:sticky lg:top-6 lg:self-start">
                        <h2 class="section-title text-lg lg:text-[22px] text-[var(--charcoal)] font-bold mb-3">Booking
                            Summary</h2>

                        <!-- Cart Items Summary -->
                        <div class="booking-summary">
                            <div v-if="cartItemLength > 0"
                                class="cart-summary-list bg-[linear-gradient(111.97deg,_#F4ECE1_0%,_#F5F3F0_100%)] rounded-2xl shadow-[0px_1.4px_2.8px_0px_#0000000D] mb-6 border border-[var(--bone)] p-4">
                                <div v-for="(item, slotId) in cartItem" :key="slotId"
                                    class="cart-summary-item w-full mb-4 last:mb-0">
                                    <div class="item-name text-lg text-[var(--charcoal-dark)] font-medium">{{
                                        item.package_name }}</div>
                                    <div class="item-details text-base text-[var(--tone-gray)]">
                                        <span class="item-date">
                                            {{ formatDate(item.date) }}
                                            <span v-if="item.package_has_slots">@ {{ item.time_date }}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <p v-else class="summary-placeholder text-lg text-[var(--charcoal-dark)]">No tours selected
                            </p>
                        </div>

                        <!-- Totals -->
                        <div
                            class="summary-totals bg-white rounded-2xl mb-6 border border-[var(--bone)] shadow-[0px_1.4px_2.8px_0px_#0000000D] p-4">
                            <div
                                class="summary-row flex items-center justify-between text-base text-[var(--charcoal-dark)]">
                                <span>Subtotal</span>
                                <span>{{ currencyFormat(calculateSubtotal) }}</span>
                            </div>

                            <!-- Itemized Add-ons -->
                            <div v-if="sidebarAddonLines.length > 0"
                                class="addons-breakdown my-2 py-2 border-t border-b border-[var(--bone)]">
                                <div class="addons-header text-lg text-[var(--charcoal-dark)] font-medium">Add-ons:
                                </div>
                                <div v-for="addon in sidebarAddonLines" :key="addon.id"
                                    class="summary-row addon-item flex items-center justify-between gap-3 text-base text-[var(--charcoal-dark)]">
                                    <span class="addon-label-wrap group relative min-w-0 flex-1 cursor-default">
                                        <span class="block truncate">{{ addon.label }}</span>
                                        <span
                                            class="addon-tooltip pointer-events-none absolute bottom-full left-0 z-10 mb-1.5 w-max max-w-[240px] whitespace-normal rounded-lg bg-[var(--charcoal-dark)] px-2.5 py-1.5 text-sm font-normal text-white opacity-0 shadow-lg translate-y-1 transition-all duration-200 ease-out group-hover:opacity-100 group-hover:translate-y-0">
                                            {{ addon.label }}
                                        </span>
                                    </span>
                                    <span class="flex-shrink-0 font-medium">{{ currencyFormat(addon.subtotal) }}</span>
                                </div>
                            </div>

                            <div
                                class="summary-row flex items-center justify-between text-base text-[var(--tone-gray)] mb-2">
                                <span>Taxes & Fees</span>
                                <span>{{ currencyFormat(calculateFees) }}</span>
                            </div>
                            <div
                                class="summary-total border-t border-[var(--bone)] pt-2 text-xl text-[var(--charcoal-dark)] font-bold flex justify-between">
                                <span>Total</span>
                                <span>{{ currencyFormat(calculateGrandTotal) }}</span>
                            </div>
                        </div>

                        <button class="continue-button btn-primary w-full" @click="continueToCheckout">
                            Continue to {{ nextStepName }} →
                        </button>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { reactive, computed, ref } from 'vue';
import NavBtns from './Nav/NavBtns.vue';
import FlowNavigation from './Nav/FlowNavigation.vue';
import NumberInput from './Forms/AddonInput/NumberInput.vue';
import RadioInput from './Forms/AddonInput/RadioInput.vue';
import CheckboxInput from './Forms/AddonInput/CheckboxInput.vue';
import DropdownInput from './Forms/AddonInput/DropdownInput.vue';
import TextInput from './Forms/AddonInput/TextInput.vue';
import TextboxInput from './Forms/AddonInput/TextboxInput.vue';
import { formatCurrencyIntl } from '../utils/currency';
import { getMomentTimezone } from '../utils/dateUtils';
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
    name: 'AddonsNew',
    title: 'Native American Tours',
    components: {
        NavBtns,
        FlowNavigation,
        NumberInput,
        RadioInput,
        CheckboxInput,
        DropdownInput,
        TextInput,
        TextboxInput
    },
    setup() {
        const internalValues = reactive({});
        const errors = reactive({});
        const display_errors = ref(false);

        const totalCost = computed(() => {
            let total = 0;
            if (internalValues && typeof internalValues === 'object') {
                Object.values(internalValues).forEach(item => {
                    total += (item.subtotal || 0) + (item.fee || 0);
                });
            }
            return total;
        });

        return { internalValues, totalCost, errors, display_errors };
    },
    data() {
        return {
            loading: true,
            tenantId: null,
            tourOperatorId: 0,
            comboIds: 0,
            packageId: 0,
            affiliateId: 0,
            cartItem: {},
            cartItemLength: 0,
            form: null,
            addonFields: [],
            currency: 'USD',
            serviceCommission: 0,
            totalPeople: 0,
            personPaxMap: {},
            rateGroupData: {},
            peopleGroupData: [],
            showDescriptions: reactive({}) // Track which descriptions are visible
        };
    },
    computed: {
        tabs() { return 3; },
        hasAddons() { return this.addonFields.length > 0; },
        nextStepName() {
            if (this.comboIds && this.comboIds.toString().split(',').length > 1) {
                return 'Tours';
            }
            return 'Checkout';
        },
        sortedFields() {
            if (!this.form || !this.form.fields) return [];
            return [...this.form.fields]
                .filter(f => !f.parent_field_id)
                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        },
        safeValues() {
            return this.internalValues && typeof this.internalValues === 'object' && this.internalValues !== null
                ? this.internalValues
                : {};
        },
        calculateSubtotal() {
            return Object.values(this.cartItem).reduce((sum, item) => {
                return sum + (Number(item.subtotal) || 0);
            }, 0);
        },
        addonsWithPricing() {
            const addons = [];
            this.getAllFields().forEach(field => {
                const entry = this.safeValues[field.id];
                if (entry && entry.subtotal > 0) {
                    addons.push({
                        id: field.id,
                        label: field.label,
                        subtotal: entry.subtotal,
                        fee: entry.fee
                    });
                }
            });
            return addons;
        },
        // Display-only breakdown for the Booking Summary sidebar: expands
        // price_per_pax fields into one line per person (instead of a single
        // combined total) so it's clear who each charge belongs to. Totals
        // below are still driven by addonsWithPricing/calculateAddonsSubtotal.
        sidebarAddonLines() {
            const lines = [];

            this.getAllFields().forEach(field => {
                // Genuine per-pax fields are broken out per-person below; display_once
                // fields are answered once, so they stay in this single-line list even
                // though entry.subtotal is already priced across all travelers.
                if (field.unit_type === 'price_per_pax' && !field.display_once) return;
                const entry = this.safeValues[field.id];
                if (entry && entry.subtotal > 0) {
                    lines.push({ id: `${field.id}`, label: field.label, subtotal: entry.subtotal });
                }
            });

            this.pricePerPaxMatrix.forEach((personRow, personIdx) => {
                personRow.fields.forEach(field => {
                    const subtotal = this.getParentPerPaxSubtotal(field, personIdx);
                    if (subtotal <= 0) return;

                    let detail = '';
                    const value = this.safeValues[field.id]?.values?.[personIdx];
                    if (['radio', 'dropdown'].includes(field.type) && field.options && value) {
                        const selectedOption = field.options.find(opt => opt.value === value);
                        if (selectedOption) detail = ` (${selectedOption.label})`;
                    }

                    lines.push({
                        id: `${field.id}-${personIdx}`,
                        label: `${field.label} — Person ${personRow.person.personId}${detail}`,
                        subtotal
                    });
                });
            });

            return lines;
        },
        calculateAddonsSubtotal() {
            return this.addonsWithPricing.reduce((sum, addon) => sum + addon.subtotal, 0);
        },
        calculateAddonsFees() {
            return this.addonsWithPricing.reduce((sum, addon) => sum + addon.fee, 0);
        },
        calculateFees() {
            const baseFees = Object.values(this.cartItem).reduce((sum, item) => {
                return sum + (Number(item.fees) || 0);
            }, 0);
            return baseFees + this.calculateAddonsFees;
        },
        calculateGrandTotal() {
            return this.calculateSubtotal + this.calculateAddonsSubtotal + this.calculateFees;
        },
        personsList() {
            const persons = [];
            const firstItem = Object.values(this.cartItem).find(
                item => item.package_id === this.packageId
            );

            if (!firstItem || !firstItem.rate_group || !firstItem.people_group) {
                return persons;
            }

            const rateGroup = firstItem.rate_group;
            const peopleGroup = firstItem.people_group;
            const rates = firstItem.rates || [];
            const rateGroupIds = firstItem.rate_group_ids || [];
            // Group subtotal (rate + permit fee + tax, summed for everyone in this rate
            // group) — set by Init.vue's calculateTotals(). Dividing by the group's
            // headcount gives the per-person subtotal used for the price badge below.
            const calculation = firstItem.calculation || [];
            const slotId = Object.keys(this.cartItem).find(
                slotId => this.cartItem[slotId].package_id === this.packageId
            );

            const paxRecords = firstItem.pax || firstItem.passengers || [];

            let personOrdinal = 0;
            Object.keys(rateGroup).forEach((rateType, rateIdx) => {
                const count = peopleGroup[rateIdx] || 0;
                const rateGroupId = rateGroupIds[rateIdx] || null; // tour_package_rate_id for this rate group
                const ratePrice = rates[rateIdx] || 0; // Get rate price for this rate type

                const groupSubtotal = Number(calculation[rateIdx] || 0);
                const perPersonSubtotal = count > 0 ? this.roundout(groupSubtotal / count) : 0;

                for (let i = 0; i < count; i++) {
                    const personKey = `person-${personOrdinal + 1}`;
                    const paxIndex = this.personPaxMap[personKey] || this.generatePaxIndex(slotId, personOrdinal + 1);

                    personOrdinal++;

                    persons.push({
                        personId: personOrdinal,
                        personKey: personKey,
                        paxIndex: paxIndex,
                        paxId: rateGroupId, // Store rate group ID instead of passenger ID
                        rateType: rateType,
                        rateLabel: rateGroup[rateType],
                        rate: ratePrice, // Base ticket rate (no fees)
                        total: perPersonSubtotal, // Rate + permit fee + tax (no platform fee)
                        displayName: `Person ${personOrdinal} · ${rateGroup[rateType]}`
                    });
                }
            });

            return persons;
        },
        pricePerPaxMatrix() {
            const matrix = [];

            if (this.personsList.length === 0) {
                return matrix;
            }

            // Get all price_per_pax parent fields — display_once fields are answered
            // once (rendered in the general "Trip Options" section instead) even
            // though they're still priced/stored per traveler, so they're excluded here.
            const pricePerPaxFields = this.sortedFields.filter(field =>
                field.unit_type === 'price_per_pax' && !field.display_once && this.shouldShowField(field)
            );

            // Only create matrix if there are price_per_pax fields
            if (pricePerPaxFields.length === 0) {
                return matrix;  // Return empty matrix to avoid showing empty person sections
            }

            // Create a row for each person with all their price_per_pax fields
            this.personsList.forEach(person => {
                matrix.push({
                    person: person,
                    fields: pricePerPaxFields
                });
            });

            return matrix;
        }
    },
    watch: {
        totalPeople() {
            if (this.form && this.form.fields) {
                this.updateAllFees();
            }
        },
        serviceCommission() {
            if (this.form && this.form.fields) {
                this.updateAllFees();
            }
        },
        personsList: {
            handler(newPersons) {
                newPersons.forEach(person => {
                    if (!this.personPaxMap[person.personKey]) {
                        this.personPaxMap[person.personKey] = person.paxIndex;
                    }
                });
            },
            immediate: true
        }
    },
    async created() {
        this.initializeState();
        await this.fetchAddonForm();
        this.$store.dispatch('storeTabs', this.tabs);
        this.$store.dispatch('storeMindChange', 0);
    },
    methods: {
        generatePaxIndex(slotId, personOrdinal) {
            const timestamp = Date.now();
            const random = Math.random().toString(36).substring(2, 15);
            return `${slotId || this.packageId}-${timestamp}-${random}-${personOrdinal}`;
        },
        initializeState() {
            this.tenantId = this.$store.state.tenantId;
            this.tourOperatorId = this.$store.state.tourOperatorId;
            this.packageId = this.$store.state.packageId;
            this.affiliateId = this.$store.state.affiliateId;
            this.comboIds = this.$store.state.comboIds;
            this.cartItem = this.$store.state.cartItem;
            this.cartItemLength = Object.keys(this.cartItem).length;
            this.currency = this.$store.state.currency || 'USD';

            // Calculate total people from cart
            // const firstItem = Object.values(this.cartItem)[0];
            const firstItem = Object.values(this.cartItem).find(
                item => item.package_id === this.packageId
            );
            if (firstItem && firstItem.people_group) {
                this.serviceCommission = firstItem.service_commission || 0;
                this.totalPeople = firstItem.people_group.reduce((sum, count) => sum + Number(count || 0), 0);
            }
        },
        async fetchAddonForm() {
            this.loading = true;
            try {
                const response = await axios.get(`/package/get-addon-form/${this.packageId}`);

                if (response.data.success && response.data.form) {
                    this.form = response.data.form;
                    this.addonFields = response.data.form.fields || [];

                    // Set hasCustomFields in Vuex so navigation shows "Add Extras"
                    const hasFields = this.addonFields.length > 0;
                    this.$store.dispatch('storeHasCustomFields', hasFields);

                    this.initializeValues(this.addonFields);

                    // Restore custom_fields if user is returning to this page
                    // const firstSlotId = Object.keys(this.cartItem)[0];
                    const firstSlotId = Object.keys(this.cartItem).find(
                        slotId => this.cartItem[slotId].package_id === this.packageId
                    );
                    if (firstSlotId && this.cartItem[firstSlotId]?.custom_fields) {
                        this.restoreCustomFields(this.cartItem[firstSlotId].custom_fields);
                    }

                    this.updateAllFees();
                } else {
                    // No form data returned, hide Add Extras navigation
                    this.addonFields = [];
                    this.$store.dispatch('storeHasCustomFields', false);
                }
            } catch (error) {
                console.error('Failed to fetch addon form:', error);
                this.addonFields = [];
                this.$store.dispatch('storeHasCustomFields', false);

                if (error?.response?.status !== 404) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed to Load Add-ons',
                        text: 'Could not load additional options. Please try again.',
                        confirmButtonColor: '#0D9488'
                    });
                }
            } finally {
                this.loading = false;
            }
        },
        getInputComponent(type) {
            switch (type) {
                case 'number': return 'NumberInput';
                case 'radio': return 'RadioInput';
                case 'checkbox': return 'CheckboxInput';
                case 'dropdown': return 'DropdownInput';
                case 'text': return 'TextInput';
                case 'textbox': return 'TextboxInput';
                default: return null;
            }
        },
        initializeValues(fields) {
            if (!fields) return;
            fields.forEach(field => {
                // Only initialize if not already in safeValues
                if (!(field.id in this.safeValues)) {
                    // display_once price_per_pax fields are answered once — treat them
                    // like a regular (non-repeated) field; updateAllFees()/buildCustomFields()
                    // fan the single answer out per traveler for pricing/storage.
                    const isRepeated = ['price_per_unit', 'price_per_pax'].includes(field.unit_type) && !field.display_once;
                    this.safeValues[field.id] = {
                        value: this.defaultForType(field.type),
                        price: 0,
                        subtotal: 0,
                        fee: 0,
                        isRepeated,
                        values: isRepeated ? [] : null,
                        parentValues: field.children ? [] : null // only for children of per-pax parents
                    };
                }

                if (field.children && field.children.length) {
                    this.initializeValues(field.children);
                }
            });
        },
        defaultForType(type) {
            switch (type) {
                case 'checkbox': return false;
                case 'number': return 0;
                case 'radio':
                case 'dropdown': return '';
                case 'text':
                case 'textbox': return '';
                default: return '';
            }
        },
        getFieldValue(field) {
            return this.safeValues[field.id]?.value ?? this.defaultForType(field.type);
        },
        getChildValue(child, index) {
            return this.safeValues[child.id]?.values?.[index] ?? this.defaultForType(child.type);
        },
        getRepeatCount(field) {
            return this.safeValues[field.id]?.value || 0;
        },
        shouldShowField(field) {
            // Only show fields with visibility 'Both' or 'Frontend'
            // Hide 'Backend' visibility type
            if (!field.visibility || field.visibility === 'both' || field.visibility === 'frontend') {
                return true;
            }
            if (field.visibility === 'backend') {
                return false;
            }
            return true;
        },
        shouldShowChildren(field) {
            if (!field.children || field.children.length === 0) return false;

            // If no rules, always show children
            if (!field.rules || field.rules.length === 0) return true;

            // Check if any child has a value (for editing scenarios)
            const hasChildrenWithValues = field.children.some(child => {
                const childEntry = this.safeValues[child.id];
                if (!childEntry) return false;

                // Check for repeated children with values
                if (childEntry.isRepeated && childEntry.values && childEntry.values.length > 0) {
                    return childEntry.values.some(val => {
                        if (child.type === 'checkbox') return val === true;
                        if (child.type === 'number') return Number(val) > 0;
                        return val !== '' && val !== null && val !== undefined;
                    });
                }

                // Check for single value children
                const value = childEntry.value;
                if (child.type === 'checkbox') return value === true;
                if (child.type === 'number') return Number(value) > 0;
                return value !== '' && value !== null && value !== undefined;
            });

            // Show children if they have values (editing scenario) OR if parent rule is met
            if (hasChildrenWithValues) return true;

            // Otherwise check parent field rule
            const rule = field.rules[0];
            const value = this.safeValues[field.id]?.value ?? null;

            if (value === undefined || value === null) return false;

            switch (rule.operator) {
                case 'checked': return !!value;
                case 'equals':
                case '==': return value == rule.value;
                case '!=': return value != rule.value;
                case '>': return Number(value) > Number(rule.value);
                case '<': return Number(value) < Number(rule.value);
                case '>=': return Number(value) >= Number(rule.value);
                case '<=': return Number(value) <= Number(rule.value);
                default: return false;
            }
        },
        sortedChildren(children) {
            if (!children) return [];
            return [...children].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        },
        updateValue(field, newValue) {
            const isRepeated = ['price_per_unit', 'price_per_pax'].includes(field.unit_type) && !field.display_once;
            if (!this.safeValues[field.id]) {
                this.safeValues[field.id] = {
                    value: newValue,
                    price: 0,
                    subtotal: 0,
                    fee: 0,
                    isRepeated,
                    values: isRepeated ? [] : null
                };
            } else {
                this.safeValues[field.id].value = newValue;
            }
            // this.initializeValues(this.form.fields);
            this.updateAllFees();
        },
        updateChildValue(child, index, newValue) {
            if (!this.safeValues[child.id]) {
                this.safeValues[child.id] = {
                    value: this.defaultForType(child.type),
                    price: 0,
                    subtotal: 0,
                    fee: 0,
                    isRepeated: false,
                    values: []
                };
            }
            if (!this.safeValues[child.id].values) {
                this.safeValues[child.id].values = [];
            }
            this.safeValues[child.id].values[index] = newValue;
            this.updateAllFees();
        },
        // Price per pax parent field methods
        getParentPerPaxValue(field, index) {
            return this.safeValues[field.id]?.values?.[index] ?? this.defaultForType(field.type);
        },
        updateParentPerPaxValue(field, index, newValue) {
            if (!this.safeValues[field.id]) {
                this.safeValues[field.id] = {
                    value: this.defaultForType(field.type),
                    price: 0,
                    subtotal: 0,
                    fee: 0,
                    isRepeated: true,
                    values: []
                };
            }
            if (!this.safeValues[field.id].values) {
                this.safeValues[field.id].values = [];
            }
            this.safeValues[field.id].values[index] = newValue;
            this.updateAllFees();
        },
        shouldShowChildrenForPerPaxParent(field, index) {
            if (!field.children || field.children.length === 0) return false;
            if (!field.rules || field.rules.length === 0) return true;

            const rule = field.rules[0];
            const value = this.safeValues[field.id]?.values?.[index] ?? null;

            if (value === undefined || value === null) return false;

            switch (rule.operator) {
                case 'checked': return !!value;
                case 'equals':
                case '==': return value == rule.value;
                case '!=': return value != rule.value;
                case '>': return Number(value) > Number(rule.value);
                case '<': return Number(value) < Number(rule.value);
                case '>=': return Number(value) >= Number(rule.value);
                case '<=': return Number(value) <= Number(rule.value);
                default: return false;
            }
        },
        getChildValueForPerPaxParent(child, parentIndex) {
            // For Price per pax parent, child values are stored in a nested array structure
            // safeValues[child.id].parentValues[parentIndex] for single child value per parent
            if (!this.safeValues[child.id]) return this.defaultForType(child.type);
            if (!this.safeValues[child.id].parentValues) return this.defaultForType(child.type);
            return this.safeValues[child.id].parentValues[parentIndex] ?? this.defaultForType(child.type);
        },
        updateChildValueForPerPaxParent(child, parentIndex, newValue) {
            if (!this.safeValues[child.id]) {
                this.safeValues[child.id] = {
                    value: this.defaultForType(child.type),
                    price: 0,
                    subtotal: 0,
                    fee: 0,
                    isRepeated: false,
                    parentValues: []
                };
            }
            if (!this.safeValues[child.id].parentValues) {
                this.safeValues[child.id].parentValues = [];
            }
            this.safeValues[child.id].parentValues[parentIndex] = newValue;
            this.updateAllFees();
        },
        getParentPerPaxSubtotal(field, index) {
            // Calculate subtotal for this specific person's selection
            const value = this.safeValues[field.id]?.values?.[index];
            if (!value) return 0;

            let subtotal = 0;
            if (field.type === 'radio' || field.type === 'dropdown') {
                const selectedOption = field.options?.find(opt => opt.value === value);
                if (selectedOption && selectedOption.additional_fee && selectedOption.price) {
                    subtotal = Number(selectedOption.price) || 0;
                }
            } else if (field.additional_fee && field.price) {
                subtotal = Number(field.price) || 0;
            }

            // Add child subtotals for this parent instance
            if (field.children) {
                field.children.forEach(child => {
                    const childValue = this.getChildValueForPerPaxParent(child, index);
                    if (childValue) {
                        if (child.type === 'radio' || child.type === 'dropdown') {
                            const selectedOption = child.options?.find(opt => opt.value === childValue);
                            if (selectedOption && selectedOption.additional_fee && selectedOption.price) {
                                subtotal += Number(selectedOption.price) || 0;
                            }
                        } else if (child.additional_fee && child.price) {
                            if (child.type === 'checkbox' && childValue === true) {
                                subtotal += Number(child.price) || 0;
                            } else if (child.type === 'number') {
                                subtotal += Number(child.price) * Number(childValue || 0);
                            } else {
                                subtotal += Number(child.price) || 0;
                            }
                        }
                    }
                });
            }

            return this.roundout(subtotal);
        },
        getPersonSubtotal(personIndex) {
            // Calculate total subtotal for all price_per_pax fields for this person
            if (!this.pricePerPaxMatrix[personIndex]) return 0;

            const personRow = this.pricePerPaxMatrix[personIndex];
            let total = 0;

            personRow.fields.forEach(field => {
                total += this.getParentPerPaxSubtotal(field, personIndex);
            });

            return this.roundout(total);
        },
        resizeChildValues(child, parent, commissionRate) {
            const item = this.safeValues[child.id];
            if (!item) return;

            let targetCount = 0;
            if (parent.unit_type === 'price_per_unit') {
                targetCount = Number(this.safeValues[parent.id]?.value || 0);
            } else if (parent.unit_type === 'price_per_pax') {
                targetCount = Number(this.totalPeople) || 0;
            }

            // Use parentValues for per-pax children, values for per-unit
            const arrayKey = parent.unit_type === 'price_per_pax' ? 'parentValues' : 'values';
            let values = item[arrayKey] || [];

            // Only resize if needed
            if (values.length < targetCount) {
                // Pad with defaults
                while (values.length < targetCount) {
                    values.push(this.defaultForType(child.type));
                }
            } else if (values.length > targetCount) {
                // Truncate excess
                values = values.slice(0, targetCount);
            }

            item[arrayKey] = values;

            // Recalculate subtotal & fee
            let subtotal = 0;
            let totalFee = 0;

            values.forEach(val => {
                let instanceCost = 0;
                if (child.type === 'radio' || child.type === 'dropdown') {
                    const opt = child.options?.find(o => o.value === val);
                    if (opt?.additional_fee && opt.price) {
                        instanceCost = Number(opt.price) || 0;
                    }
                } else if (child.type === 'checkbox') {
                    if (val === true && child.additional_fee && child.price) {
                        instanceCost = Number(child.price) || 0;
                    }
                } else if (child.type === 'number' && child.additional_fee && child.price) {
                    instanceCost = Number(child.price) * Number(val || 0);
                } else if (child.additional_fee && child.price) {
                    instanceCost = Number(child.price) || 0;
                }

                subtotal += instanceCost;
                totalFee += this.roundout(instanceCost * commissionRate);;
            });

            item.subtotal = this.roundout(subtotal);
            item.fee = this.roundout(totalFee);
        },
        updateAllFees() {
            const commissionRate = Number(this.serviceCommission) / 100 || 0;
            const allFields = this.getAllFields();

            allFields.forEach(field => {
                const entry = this.safeValues[field.id];
                if (!entry) return;

                let price = 0;
                let subtotal = 0;
                let fee = 0;

                // Handle parent fields with Price per pax unit type (repeated parent)
                if (!field.parent_field_id && field.unit_type === 'price_per_pax' && entry.values && entry.values.length > 0) {
                    entry.values.forEach(val => {
                        if (field.type === 'radio' || field.type === 'dropdown') {
                            if (val && field.options) {
                                const selectedOption = field.options.find(option => option.value === val);
                                if (selectedOption && selectedOption.additional_fee && selectedOption.price) {
                                    const instancePrice = Number(selectedOption.price) || 0;
                                    subtotal += instancePrice;
                                    fee += this.roundout(instancePrice * commissionRate);
                                }
                            }
                        } else if (field.additional_fee && field.price) {
                            const instancePrice = Number(field.price) || 0;
                            subtotal += instancePrice;
                            fee += this.roundout(instancePrice * commissionRate);
                        }
                    });
                    price = subtotal > 0 ? this.roundout(subtotal / entry.values.length) : 0;
                }
                // Regular field handling (not Price per pax parent)
                else {
                    const value = entry.value;

                    if (field.type === 'radio' || field.type === 'dropdown') {
                        if (value && field.options) {
                            const selectedOption = field.options.find(option => option.value === value);
                            if (selectedOption && selectedOption.additional_fee && selectedOption.price) {
                                price = Number(selectedOption.price) || 0;

                                if (field.unit_type === 'price_per_pax' && Number(this.totalPeople) > 0) {
                                    // display_once field: answered once, but priced per
                                    // traveler — calculate fee for 1 pax, then multiply by total people
                                    const feePerPax = this.roundout(price * commissionRate);
                                    subtotal = this.roundout(price * Number(this.totalPeople));
                                    fee = this.roundout(feePerPax * Number(this.totalPeople));
                                } else {
                                    subtotal = price;
                                    // Fee calculation for 1 unit
                                    fee = this.roundout(price * commissionRate);
                                }
                            }
                        }
                    } else if (field.type === 'number') {
                        if (field.additional_fee && field.price) {
                            price = Number(field.price) || 0;
                            const quantity = Number(value) || 0;

                            if (field.unit_type === 'price_per_unit' && quantity > 0) {
                                // Calculate fee for 1 unit, then multiply by quantity
                                const feePerUnit = this.roundout(price * commissionRate);
                                subtotal = this.roundout(price * quantity);
                                fee = this.roundout(feePerUnit * quantity);
                            } else if (field.unit_type === 'price_per_pax' && Number(this.totalPeople) > 0) {
                                // display_once field: quantity entered once, priced per
                                // traveler — calculate fee for 1 unit, then multiply by quantity × total people
                                const feePerUnit = this.roundout(price * commissionRate);
                                const totalUnits = quantity * Number(this.totalPeople);
                                subtotal = this.roundout(price * totalUnits);
                                fee = this.roundout(feePerUnit * totalUnits);
                            } else {
                                subtotal = this.roundout(price * quantity);
                                fee = this.roundout(subtotal * commissionRate);
                            }
                        }
                    } else if (field.type === 'checkbox') {
                        if (field.additional_fee && field.price && value === true) {
                            price = Number(field.price) || 0;

                            if (field.unit_type === 'price_per_pax' && Number(this.totalPeople) > 0) {
                                // Calculate fee for 1 pax, then multiply by total people
                                const feePerPax = this.roundout(price * commissionRate);
                                subtotal = this.roundout(price * Number(this.totalPeople));
                                fee = this.roundout(feePerPax * Number(this.totalPeople));
                            } else {
                                subtotal = price;
                                fee = this.roundout(price * commissionRate);
                            }
                        }
                    } else if (field.additional_fee && field.price) {
                        price = Number(field.price) || 0;

                        if (field.unit_type === 'price_per_pax' && Number(this.totalPeople) > 0) {
                            const feePerPax = this.roundout(price * commissionRate);
                            subtotal = this.roundout(price * Number(this.totalPeople));
                            fee = this.roundout(feePerPax * Number(this.totalPeople));
                        } else {
                            subtotal = price;
                            fee = this.roundout(price * commissionRate);
                        }
                    }
                }

                entry.price = this.roundout(price);
                entry.subtotal = this.roundout(subtotal);
                entry.fee = this.roundout(fee);

                // Only resize children if parent has repeated unit type
                if (field.children && ['price_per_unit', 'price_per_pax'].includes(field.unit_type)) {
                    field.children.forEach(child => {
                        this.resizeChildValues(child, field, commissionRate);
                    });
                }
            });

            // Update children
            this.getAllFields().forEach(field => {
                if (field.children) {
                    field.children.forEach(child => {
                        if (['price_per_unit', 'price_per_pax'].includes(field.unit_type)) {
                            this.resizeChildValues(child, field, commissionRate);
                        }
                    });
                }
            });
        },
        getAllFields(fields = this.form?.fields, allFields = []) {
            if (!fields) return allFields;
            fields.forEach(field => {
                allFields.push(field);
                if (field.children) {
                    this.getAllFields(field.children, allFields);
                }
            });
            return allFields;
        },
        hasValidFieldValue(field) {
            const value = this.safeValues[field.id]?.value;

            switch (field.type) {
                case 'checkbox':
                    return value === true;
                case 'number':
                    return Number(value) > 0;
                case 'radio':
                case 'dropdown':
                    return value !== '' && value !== null && value !== undefined;
                case 'text':
                case 'textbox':
                    return value && value.trim() !== '';
                default:
                    return false;
            }
        },
        isValidPersonValue(value, fieldType) {
            // Check if a person's value is valid (not empty)
            switch (fieldType) {
                case 'checkbox':
                    return value === true;
                case 'number':
                    return Number(value) > 0;
                case 'radio':
                case 'dropdown':
                case 'select':
                    return value !== '' && value !== null && value !== undefined;
                case 'text':
                case 'textbox':
                    return value && value.trim() !== '';
                default:
                    return false;
            }
        },
        hasValidChildValue(child, index) {
            const value = this.safeValues[child.id]?.values?.[index];

            switch (child.type) {
                case 'checkbox':
                    return value === true;
                case 'number':
                    return Number(value) > 0;
                case 'radio':
                case 'dropdown':
                    return value !== '' && value !== null && value !== undefined;
                case 'text':
                case 'textbox':
                    return value && value.trim() !== '';
                default:
                    return false;
            }
        },
        validateField(field, suffix = '') {
            const fieldKey = suffix ? `${field.id}-${suffix}` : field.id;

            if (!field.required) {
                delete this.errors[fieldKey];
                return true;
            }

            // For Price per pax parent (repeated parent), validate the specific person
            if (suffix && !field.parent_field_id && field.unit_type === 'price_per_pax') {
                const index = parseInt(suffix) - 1;
                const value = this.safeValues[field.id]?.values?.[index];
                let isValid = false;

                switch (field.type) {
                    case 'checkbox':
                        isValid = value === true;
                        break;
                    case 'number':
                        isValid = Number(value) > 0;
                        break;
                    case 'radio':
                    case 'dropdown':
                        isValid = value !== '' && value !== null && value !== undefined;
                        break;
                    case 'text':
                    case 'textbox':
                        isValid = value && value.trim() !== '';
                        break;
                    default:
                        isValid = value !== null && value !== undefined && value !== '';
                }

                if (!isValid) {
                    this.errors[fieldKey] = `${field.label} is required for Person ${suffix}`;
                    return false;
                }
            }
            // For repeated children, validate the specific index
            else if (suffix) {
                const index = suffix.startsWith('p') ? parseInt(suffix.substring(1)) - 1 : parseInt(suffix) - 1;
                if (!this.hasValidChildValue(field, index)) {
                    this.errors[fieldKey] = `${field.label} is required for Person/Unit ${suffix.replace('p', '')}`;
                    return false;
                }
            }
            // Validate regular parent field
            else {
                if (!this.hasValidFieldValue(field)) {
                    this.errors[fieldKey] = `${field.label} is required`;
                    return false;
                }
            }

            delete this.errors[fieldKey];
            return true;
        },
        validateAllFields() {
            // Clear all errors (since errors is reactive, we need to delete keys instead of reassigning)
            Object.keys(this.errors).forEach(key => delete this.errors[key]);
            let isValid = true;

            const allFields = this.getAllFields();

            allFields.forEach(field => {
                if (!field.required) return;

                // Skip validation for backend visibility fields
                if (!this.shouldShowField(field)) return;

                // Check if field should be shown based on parent rules
                const parent = allFields.find(f => f.children?.some(c => c.id === field.id));
                if (parent && !this.shouldShowChildren(parent)) {
                    return; // Skip validation if parent condition not met
                }

                // Validate parent fields
                if (!field.parent_field_id) {
                    // For Price per pax parent, validate each person's selection.
                    // display_once fields are answered once (entry.value, not a
                    // per-person entry.values array), so they fall through to the
                    // regular single-value validation below instead.
                    if (field.unit_type === 'price_per_pax' && !field.display_once && this.totalPeople > 0) {
                        for (let i = 0; i < this.totalPeople; i++) {
                            const value = this.safeValues[field.id]?.values?.[i];
                            let personValid = false;

                            switch (field.type) {
                                case 'checkbox':
                                    personValid = value === true;
                                    break;
                                case 'number':
                                    personValid = Number(value) > 0;
                                    break;
                                case 'radio':
                                case 'dropdown':
                                    personValid = value !== '' && value !== null && value !== undefined;
                                    break;
                                case 'text':
                                case 'textbox':
                                    personValid = value && value.trim() !== '';
                                    break;
                                default:
                                    personValid = value !== null && value !== undefined && value !== '';
                            }

                            if (!personValid) {
                                this.errors[`${field.id}-${i + 1}`] = `${field.label} is required for Person ${i + 1}`;
                                isValid = false;
                            }

                            // Validate children for this person's parent instance
                            if (personValid && field.children && field.children.length > 0) {
                                field.children.forEach(child => {
                                    if (!child.required) return;

                                    // Check if children should show for this person's selection
                                    if (this.shouldShowChildrenForPerPaxParent(field, i)) {
                                        const childValue = this.safeValues[child.id]?.parentValues?.[i];
                                        let childValid = false;

                                        switch (child.type) {
                                            case 'checkbox':
                                                childValid = childValue === true;
                                                break;
                                            case 'number':
                                                childValid = Number(childValue) > 0;
                                                break;
                                            case 'radio':
                                            case 'dropdown':
                                                childValid = childValue !== '' && childValue !== null && childValue !== undefined;
                                                break;
                                            case 'text':
                                            case 'textbox':
                                                childValid = childValue && childValue.trim() !== '';
                                                break;
                                            default:
                                                childValid = childValue !== null && childValue !== undefined && childValue !== '';
                                        }

                                        if (!childValid) {
                                            this.errors[`${child.id}-p${i + 1}`] = `${child.label} is required for Person ${i + 1}`;
                                            isValid = false;
                                        }
                                    }
                                });
                            }
                        }
                    }
                    // Regular parent field validation
                    else if (!this.hasValidFieldValue(field)) {
                        this.errors[field.id] = `${field.label} is required`;
                        isValid = false;
                    } else if (field.children && field.children.length > 0 && this.shouldShowChildren(field)) {
                        // If parent is filled and has children, validate children
                        field.children.forEach(child => {
                            if (!child.required) return;

                            // For repeated children (not for display_once parents — their
                            // children are answered once, same as any regular field's children)
                            if (field.unit_type === 'price_per_pax' && !field.display_once) {
                                for (let i = 0; i < this.totalPeople; i++) {
                                    if (!this.hasValidChildValue(child, i)) {
                                        this.errors[`${child.id}-${i + 1}`] = `${child.label} is required for Person ${i + 1}`;
                                        isValid = false;
                                    }
                                }
                            } else if (field.unit_type === 'price_per_unit') {
                                const count = this.safeValues[field.id]?.value || 0;
                                for (let i = 0; i < count; i++) {
                                    if (!this.hasValidChildValue(child, i)) {
                                        this.errors[`${child.id}-${i + 1}`] = `${child.label} is required for Unit ${i + 1}`;
                                        isValid = false;
                                    }
                                }
                            } else {
                                // Regular child (not repeated)
                                if (!this.hasValidFieldValue(child)) {
                                    this.errors[child.id] = `${child.label} is required`;
                                    isValid = false;
                                }
                            }
                        });
                    }
                }
            });

            return isValid;
        },
        roundout(num) {
            return Math.round(num * 100) / 100;
        },
        // Label suffix for display_once price_per_pax fields, so the badge reads
        // e.g. "$20.00 per person" instead of implying a flat one-time price.
        perPersonSuffix(field) {
            return (field.unit_type === 'price_per_pax' && !!field.display_once) ? ' per person' : '';
        },
        currencyFormat(amount) {
            return formatCurrencyIntl(amount, this.currency);
        },
        formatUnitType(unitType) {
            if (!unitType) return 'N/A';
            const typeMap = {
                'price_per_unit': 'Price Per Unit',
                'price_per_pax': 'Price Per Person',
                'one_time': 'One Time',
                'price_per_night': 'Price Per Night',
                'price_per_day': 'Price Per Day'
            };
            return typeMap[unitType] || unitType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        },
        formatDate(date) {
            return getMomentTimezone(this.$store.state.timezone, date).format('ddd, MMM D, YYYY');
        },
        navigateToTab(tab, destination) {
            if ([1, 2, 3, 4, 5, 6].includes(tab)) {
                if (tab === 2) {
                    this.$store.dispatch('storeFormData', null);
                }
                this.$store.dispatch('storeMindChange', 1);
                this.$router.push({ name: destination });
            }
        },
        buildCustomFields() {
            const customFields = [];
            const parentFields = this.sortedFields; // Only parent fields (no parent_field_id)
            const commissionRate = Number(this.serviceCommission) / 100 || 0;

            parentFields.forEach(field => {
                const entry = this.safeValues[field.id];
                if (!entry) return;

                // Check if parent field has Price per pax (repeated parent) — either a
                // genuine per-traveler field (one answer per person, stored in
                // entry.values) or a display_once field (answered once, in entry.value,
                // but still priced/stored per traveler so the backend sees the same shape)
                const isGenuinePerPax = field.unit_type === 'price_per_pax' && !field.display_once && entry.values && entry.values.length > 0;
                const isDisplayOncePax = field.unit_type === 'price_per_pax' && !!field.display_once && this.hasValidFieldValue(field);
                const isPerPaxParent = isGenuinePerPax || isDisplayOncePax;
                // Fan the single display_once answer out into one identical "instance"
                // per traveler so personData[] below is indistinguishable from a genuine
                // per-pax field — TourBookingAddonService::saveFormAddons() has no
                // display_once awareness at all; it just expects a personData array.
                const perPaxValues = isGenuinePerPax
                    ? entry.values
                    : (isDisplayOncePax ? this.personsList.map(() => entry.value) : null);

                // Check if field has a valid value
                if (this.hasValidFieldValue(field) || isPerPaxParent) {
                    // For dropdown/radio, pricing might be in selected option (not field.additional_fee)
                    // So check if entry has calculated price/subtotal to determine if pricing is enabled
                    const hasPricing = field.additional_fee || entry.price > 0 || entry.subtotal > 0;

                    // Find selected option for dropdown/radio fields
                    let selectedOption = null;
                    if (['select', 'radio', 'dropdown'].includes(field.type) && field.options && entry.value) {
                        selectedOption = field.options.find(opt => opt.value === entry.value);
                    }

                    // Calculate field-level and option-level pricing separately
                    const fieldPrice = field.additional_fee && field.price ? Number(field.price) : 0;
                    const fieldFee = fieldPrice > 0 ? this.roundout(fieldPrice * commissionRate) : 0;

                    const optionPrice = selectedOption?.additional_fee && selectedOption?.price ? Number(selectedOption.price) : 0;
                    const optionFee = optionPrice > 0 ? this.roundout(optionPrice * commissionRate) : 0;

                    // Calculate quantity for price_per_unit fields
                    const quantity = field.unit_type === 'price_per_unit' ? (Number(entry.value) || 1) : 1;
                    
                    // Extract rule_id from field.rules or option pricing
                    const ruleId = selectedOption?.pricing_rule_id || 
                                   field.pricing_rule_id || 
                                   (field.rules && field.rules.length > 0 ? field.rules[0].id : null);

                    const customField = {
                        // Frontend display fields (existing - keep as is)
                        id: field.id,
                        name: field.label,
                        type: field.type,
                        value: entry.value,
                        priceInfo: {
                            enabled: hasPricing,
                            price: entry.price || 0,
                            subtotal: entry.subtotal || 0,
                            fee: entry.fee || 0,
                            quantity: quantity
                        },
                        
                        // Database fields - added directly (flattened structure)
                        form_id: field.form_id || null,
                        field_id: field.id,
                        field_label: field.label,
                        option_id: selectedOption?.id || null,
                        option_label: selectedOption?.label || selectedOption?.value || null,
                        rule_id: ruleId,
                        field_additional_fee: field.additional_fee || 0,
                        field_unit_type: field.unit_type || null,
                        category_id: field.category_id || null,
                        category_name: field.category_name || null,
                        package_id: field.package_id || null,
                        package_name: field.package_name || null,

                        // Field-level pricing (when field itself has pricing)
                        field_price: fieldPrice,
                        field_fee: fieldFee,
                        
                        // Option-level pricing (for dropdown/radio with priced options)
                        option_additional_fee: selectedOption?.additional_fee || 0,
                        option_price: optionPrice,
                        option_fee: optionFee
                    };

                    // Add values if repeated (Price per pax parent or Price per unit/pax child)
                    if (entry.isRepeated && entry.values && entry.values.length > 0) {
                        customField.values = entry.values;
                        customField.isRepeated = true;
                    }

                    // Add person metadata for price_per_pax fields
                    if (isPerPaxParent && perPaxValues && perPaxValues.length > 0) {
                        // Build children mapping for each child field (for nesting in personData)
                        const childrenByPerson = {}; // { personIdx: [childObjects] }
                        
                        // Process children first to know what to attach to each person
                        if (field.children && field.children.length > 0) {
                            field.children.forEach(child => {
                                const childEntry = this.safeValues[child.id];
                                if (!childEntry) return;

                                // Check if child has a valid value
                                let hasValue = false;
                                if (childEntry.parentValues && childEntry.parentValues.length > 0) {
                                    hasValue = childEntry.parentValues.some(value => this.isValidPersonValue(value, child.type));
                                } else {
                                    hasValue = this.hasValidFieldValue(child) ||
                                        (childEntry.isRepeated && childEntry.values && childEntry.values.length > 0);
                                }

                                if (hasValue && childEntry.parentValues) {
                                    // For each person, build their child object
                                    childEntry.parentValues.forEach((childValue, personIdx) => {
                                        if (!this.isValidPersonValue(childValue, child.type)) {
                                            return; // Skip empty values
                                        }
                                        
                                        // Initialize person's children array if needed
                                        if (!childrenByPerson[personIdx]) {
                                            childrenByPerson[personIdx] = [];
                                        }
                                        
                                        // Find option if dropdown/radio
                                        let childPersonOption = null;
                                        if (['select', 'radio', 'dropdown'].includes(child.type) && child.options && childValue) {
                                            childPersonOption = child.options.find(opt => opt.value === childValue);
                                        }
                                        
                                        // Calculate pricing
                                        const childPersonOptionPrice = childPersonOption?.additional_fee && childPersonOption?.price ? Number(childPersonOption.price) : 0;
                                        const childPersonOptionFee = childPersonOptionPrice > 0 ? this.roundout(childPersonOptionPrice * commissionRate) : 0;
                                        
                                        // Extract rule_id
                                        const childPersonRuleId = childPersonOption?.pricing_rule_id || 
                                                                  child.pricing_rule_id || 
                                                                  (child.rules && child.rules.length > 0 ? child.rules[0].id : null);
                                        
                                        // Build rule snapshot
                                        let childPersonRuleSnapshot = null;
                                        if (childPersonRuleId && child.rules && child.rules.length > 0) {
                                            const rule = child.rules[0];
                                            childPersonRuleSnapshot = {
                                                value: rule.value || null,
                                                operator: rule.operator || null,
                                                option_id: rule.option_id || null
                                            };
                                        }
                                        
                                        // Create child object for this person
                                        childrenByPerson[personIdx].push({
                                            form_id: child.form_id || field.form_id || null,
                                            field_id: child.id,
                                            field_label: child.label,
                                            type: child.type,
                                            value: childValue,
                                            parent_field_id: field.id,
                                            option_id: childPersonOption?.id || null,
                                            option_label: childPersonOption?.label || null,
                                            rule_id: childPersonRuleId,
                                            applied_rule_snapshot: childPersonRuleSnapshot,
                                            option_additional_fee: childPersonOption?.additional_fee || 0,
                                            option_price: childPersonOptionPrice,
                                            option_fee: childPersonOptionFee
                                        });
                                    });
                                }
                            });
                        }
                        
                        // Filter out persons with empty/invalid values and attach children
                        customField.personData = perPaxValues
                            .map((value, idx) => {
                                // Skip persons with empty values
                                if (!this.isValidPersonValue(value, field.type)) {
                                    return null;
                                }
                                
                                const person = this.personsList[idx];
                                
                                // For dropdown/radio fields, find the selected option for this person
                                let personOption = null;
                                if (['select', 'radio', 'dropdown'].includes(field.type) && field.options && value) {
                                    personOption = field.options.find(opt => opt.value === value);
                                }
                                
                                // Calculate option pricing for this person's selection
                                const personOptionPrice = personOption?.additional_fee && personOption?.price ? Number(personOption.price) : 0;
                                const personOptionFee = personOptionPrice > 0 ? this.roundout(personOptionPrice * commissionRate) : 0;
                                
                                // Extract rule_id for this person's option
                                const personRuleId = personOption?.pricing_rule_id || 
                                                    field.pricing_rule_id || 
                                                    (field.rules && field.rules.length > 0 ? field.rules[0].id : null);
                                
                                // Build applied_rule_snapshot if rule_id exists
                                let personRuleSnapshot = null;
                                if (personRuleId && field.rules && field.rules.length > 0) {
                                    const rule = field.rules[0];
                                    personRuleSnapshot = {
                                        value: rule.value || null,
                                        operator: rule.operator || null,
                                        option_id: rule.option_id || null
                                    };
                                }
                                
                                const personData = {
                                    value: value,
                                    pax_id: person?.paxId || null,
                                    pax_index: person?.paxIndex || null,
                                    rate_type: person?.rateType || null,
                                    rate_label: person?.rateLabel || null,
                                    person_ordinal: idx + 1,
                                    // Add option details for dropdown/radio selections
                                    option_id: personOption?.id || null,
                                    option_label: personOption?.label || null,
                                    rule_id: personRuleId,
                                    applied_rule_snapshot: personRuleSnapshot,
                                    // Option-level pricing (for dropdown/radio with priced options)
                                    option_additional_fee: personOption?.additional_fee || 0,
                                    option_price: personOptionPrice,
                                    option_fee: personOptionFee,
                                    // Attach children for this person
                                    children: childrenByPerson[idx] || []
                                };
                                
                                return personData;
                            })
                            .filter(person => person !== null); // Remove null entries
                    }

                    // For non-price_per_pax parents, add child fields at top level (existing
                    // behavior) — also applies to display_once parents, since their children
                    // (if any) render/store once, not fanned per traveler like a genuine
                    // per-pax parent's children would be
                    if ((!isPerPaxParent || isDisplayOncePax) && field.children && field.children.length > 0) {
                        const childrenWithValues = [];

                        field.children.forEach(child => {
                            const childEntry = this.safeValues[child.id];
                            if (!childEntry) return;

                            let hasValue = this.hasValidFieldValue(child) ||
                                (childEntry.isRepeated && childEntry.values && childEntry.values.length > 0);

                            if (hasValue) {
                                const childHasPricing = child.additional_fee || childEntry.price > 0 || childEntry.subtotal > 0;
                                let childSelectedOption = null;
                                if (['select', 'radio', 'dropdown'].includes(child.type) && child.options && childEntry.value) {
                                    childSelectedOption = child.options.find(opt => opt.value === childEntry.value);
                                }

                                const childFieldPrice = child.additional_fee && child.price ? Number(child.price) : 0;
                                const childFieldFee = childFieldPrice > 0 ? this.roundout(childFieldPrice * commissionRate) : 0;
                                const childOptionPrice = childSelectedOption?.additional_fee && childSelectedOption?.price ? Number(childSelectedOption.price) : 0;
                                const childOptionFee = childOptionPrice > 0 ? this.roundout(childOptionPrice * commissionRate) : 0;

                                let childQuantity = 1;
                                if (child.type === 'number' && child.unit_type === 'price_per_unit') {
                                    childQuantity = Number(childEntry.value) || 1;
                                }
                                
                                const childRuleId = childSelectedOption?.pricing_rule_id || 
                                                    child.pricing_rule_id || 
                                                    (child.rules && child.rules.length > 0 ? child.rules[0].id : null);
                                
                                const childUnitType = child.unit_type || field.unit_type || null;

                                const childField = {
                                    id: child.id,
                                    name: child.label,
                                    type: child.type,
                                    priceInfo: {
                                        enabled: childHasPricing,
                                        price: childEntry.price || 0,
                                        subtotal: childEntry.subtotal || 0,
                                        fee: childEntry.fee || 0,
                                        quantity: childQuantity
                                    },
                                    form_id: child.form_id || field.form_id || null,
                                    field_id: child.id,
                                    field_label: child.label,
                                    option_id: childSelectedOption?.id || null,
                                    option_label: childSelectedOption?.label || childSelectedOption?.value || null,
                                    rule_id: childRuleId,
                                    field_additional_fee: child.additional_fee || 0,
                                    field_unit_type: childUnitType,
                                    field_price: childFieldPrice,
                                    field_fee: childFieldFee,
                                    option_additional_fee: childSelectedOption?.additional_fee || 0,
                                    option_price: childOptionPrice,
                                    option_fee: childOptionFee
                                };

                                if (childEntry.isRepeated && childEntry.values && childEntry.values.length > 0) {
                                    childField.values = childEntry.values;
                                    childField.isRepeated = true;
                                } else {
                                    childField.value = childEntry.value;
                                }

                                childrenWithValues.push(childField);
                            }
                        });

                        if (childrenWithValues.length > 0) {
                            customField.children = childrenWithValues;
                        }
                    }

                    customFields.push(customField);
                }
            });

            return customFields;
        },
        restoreCustomFields(customFields) {
            if (!customFields || !Array.isArray(customFields)) return;

            customFields.forEach(savedField => {
                // Find the original field definition from addonFields
                const originalField = this.getAllFields(this.addonFields).find(f => f.id === savedField.id);

                // Restore parent field value
                if (this.safeValues[savedField.id]) {
                    this.safeValues[savedField.id].value = savedField.value;

                    // Restore repeated values if present
                    if (savedField.values && Array.isArray(savedField.values)) {
                        this.safeValues[savedField.id].values = [...savedField.values];
                    }

                    // Restore pricing info for parent
                    if (savedField.priceInfo) {
                        this.safeValues[savedField.id].price = savedField.priceInfo.price || 0;
                        this.safeValues[savedField.id].subtotal = savedField.priceInfo.subtotal || 0;
                        this.safeValues[savedField.id].fee = savedField.priceInfo.fee || 0;
                    }
                }

                // Restore child field values
                if (savedField.children && Array.isArray(savedField.children)) {
                    savedField.children.forEach(savedChild => {
                        if (this.safeValues[savedChild.id]) {
                            // Restore children of Price per pax parent (stored in parentValues)
                            if (savedChild.parentValues && Array.isArray(savedChild.parentValues)) {
                                this.safeValues[savedChild.id].parentValues = [...savedChild.parentValues];
                            }
                            // Restore repeated child values
                            else if (savedChild.values && Array.isArray(savedChild.values)) {
                                this.safeValues[savedChild.id].values = [...savedChild.values];
                                this.safeValues[savedChild.id].isRepeated = savedChild.isRepeated === true || savedChild.isRepeated === 'true';
                            }
                            // Restore single child value
                            else {
                                this.safeValues[savedChild.id].value = savedChild.value;
                                this.safeValues[savedChild.id].isRepeated = false;
                            }

                            // Restore pricing info
                            if (savedChild.priceInfo) {
                                this.safeValues[savedChild.id].price = savedChild.priceInfo.price || 0;
                                this.safeValues[savedChild.id].subtotal = savedChild.priceInfo.subtotal || 0;
                                this.safeValues[savedChild.id].fee = savedChild.priceInfo.fee || 0;
                            }
                        }
                    });

                    // After restoring child values, recalculate pricing for repeated children
                    // if (originalField && originalField.children && originalField.children.length > 0) {
                    //     const isRepeated = ['price_per_unit', 'price_per_pax'].includes(originalField.unit_type);
                    //     if (isRepeated) {
                    //         const commissionRate = this.serviceCommission / 100 || 0;
                    //         originalField.children.forEach(child => {
                    //             this.resizeChildValues(child, originalField, commissionRate);
                    //         });
                    //     }
                    // }
                }
            });

            // Recalculate fees after restoration
            this.updateAllFees();
        },
        toggleDescription(fieldKey) {
            // Toggle the visibility of the description for the given field key
            if (this.showDescriptions[fieldKey]) {
                delete this.showDescriptions[fieldKey];
            } else {
                this.showDescriptions[fieldKey] = true;
            }
        },
        async submit() {
            await this.continueToCheckout();
        },
        async continueToCheckout() {
            // Validate all fields
            const isValid = this.validateAllFields();

            if (!isValid) {
                this.display_errors = true;

                // Scroll to first error (no popup, just show inline errors)
                this.$nextTick(() => {
                    const firstError = document.querySelector('.input-error');
                    if (firstError) {
                        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        firstError.focus();
                    }
                });

                return;
            }

            // Build and save custom_fields to the first cart item's form
            const customFields = this.buildCustomFields();
            // const firstSlotId = Object.keys(this.cartItem)[0];
            const firstSlotId = Object.keys(this.cartItem).find(
                slotId => this.cartItem[slotId].package_id === this.packageId
            );

            if (firstSlotId && this.cartItem[firstSlotId]) {
                // Calculate addon totals
                const addonsTotal = this.roundout(this.calculateAddonsSubtotal);
                const addonsFee = this.roundout(this.calculateAddonsFees);

                // Update the cart item with custom_fields and addon pricing
                // Note: item.total should remain the base tour total (subtotal + fees)
                // The checkout will add addons_total and addons_fee separately
                const updatedCartItem = { ...this.cartItem };
                updatedCartItem[firstSlotId] = {
                    ...updatedCartItem[firstSlotId],
                    custom_fields: customFields,
                    addons_total: addonsTotal,
                    addons_fee: addonsFee
                };

                // Store updated cart
                this.$store.dispatch('storeCartItem', updatedCartItem);
            }

            // Navigate to next step
            if (this.comboIds && this.comboIds.toString().split(',').length > 1) {
                // Combo packages: set mindChange to prevent auto-redirect to Init
                this.$store.dispatch('storeMindChange', 1);
                // Redirect to Index so user can schedule more packages
                this.$router.push({ name: 'Index' });
            } else {
                // Single packages: go directly to Checkout
                this.$router.push({ name: 'Checkout' });
            }
        }
    }
};
</script>

<style scoped>
.info-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.info-icon-btn:hover {
    background-color: rgba(0, 0, 0, 0.05);
    transform: scale(1.1);
}

.info-icon-btn:focus {
    outline: 2px solid #3B82F6;
    outline-offset: 2px;
}

.field-description {
    animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.required-indicator {
    color: #EF4444;
}
</style>